import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';

// Invisible but still announced by assistive tech (display: none would drop it from both).
const visuallyHidden = (theme: Theme) => ({
  position: 'absolute' as const,
  width: theme.spacing(0.125),
  height: theme.spacing(0.125),
  padding: 0,
  margin: theme.spacing(-0.125),
  overflow: 'hidden' as const,
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap' as const,
  border: 0,
});

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    minWidth: 0,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  paper: {
    minWidth: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
  },
  tableHeader: {
    minWidth: 0,
    padding: theme.spacing(3, 3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1.5, 1.5),
    },
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    overflowWrap: 'anywhere' as const,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
  },
  tableWrapper: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden' as const,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed' as const,
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      '& caption': visuallyHidden(theme),
    },
  },
  tableHead: {
    [theme.breakpoints.down('sm')]: visuallyHidden(theme),
  },
  headerRow: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  tableBody: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  dataRow: {
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gridTemplateAreas: '"group group" "description amount" "context context"',
      alignItems: 'baseline',
      columnGap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  caption: {
    captionSide: 'top' as const,
    padding: theme.spacing(2, 3),
    color: '#000000',
    textAlign: 'left' as const,
    overflowWrap: 'anywhere' as const,
  },
  headerCell: {
    padding: theme.spacing(1.2, 1.5),
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.primary.dark,
    fontWeight: 500,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
  },
  bodyCell: {
    padding: theme.spacing(1.2, 1.5),
    verticalAlign: 'top',
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      padding: 0,
      borderBottom: 'none',
      '&:last-child': {
        padding: 0,
      },
    },
  },
  codeCell: {
    color: theme.palette.primary.main,
    fontFamily: 'monospace',
  },
  descriptionCell: {
    [theme.breakpoints.down('sm')]: {
      gridArea: 'description',
    },
  },
  descriptionLine: {
    display: 'block',
    overflowWrap: 'anywhere' as const,
    [theme.breakpoints.down('sm')]: {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  rawDescription: {
    display: 'block',
    marginTop: theme.spacing(0.25),
    color: '#000000',
    fontStyle: 'italic',
    overflowWrap: 'anywhere' as const,
  },
  framing: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
  },
  groupCell: {
    color: '#000000',
    [theme.breakpoints.down('sm')]: {
      gridArea: 'group',
      justifySelf: 'start',
      marginBottom: theme.spacing(0.5),
      padding: theme.spacing(0, 1),
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[100],
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: 1.5,
    },
  },
  amountCell: {
    color: theme.palette.primary.main,
    fontFamily: 'monospace',
    fontWeight: 500,
    textAlign: 'right' as const,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      gridArea: 'amount',
    },
  },
  amountValue: {
    display: 'block',
    minWidth: 0,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
    [theme.breakpoints.down('sm')]: {
      fontFamily: 'inherit',
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 'inherit',
    },
  },
  overrideTag: {
    display: 'block',
    marginBottom: theme.spacing(0.25),
    color: '#000000',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    [theme.breakpoints.down('sm')]: {
      order: 1,
      marginTop: theme.spacing(0.25),
      marginBottom: 0,
    },
  },
  dateCell: {
    color: '#000000',
  },
  contextCell: {
    color: '#000000',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexWrap: 'wrap',
      gridArea: 'context',
      columnGap: theme.spacing(1.5),
      marginTop: theme.spacing(0.5),
    },
  },
  contextCellEmpty: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  contextItem: {
    display: 'block',
    overflowWrap: 'anywhere' as const,
  },
  numericText: {
    fontFamily: 'monospace',
  },
  unavailableRow: {
    backgroundColor: theme.palette.background.default,
  },
}));

export { visuallyHidden };
export default useStyles;
